import React, { useEffect, useState } from 'react';
import Dashboard, { DashboardAccessVisibility, DashboardUserGroup, UserGroupLevel } from 'domain/entity/dashboard';
import DashboardShareModal from 'adapter/in/dashboards/pages/Modals/ShareModal';
import i18n from 'asset/translation';
import { isSome, none, Option, some } from 'fp-ts/lib/Option';
import { dispatchUserGroups, useUserGroups } from 'adapter/in/dashboards/context/UserGroups';
import { useUpdateAccessDashboard } from 'adapter/in/dashboards/hooks/Dashboard/useUpdateDashboard';

export const SHARE_OPTIONS = { edit: 'EDIT', view: 'VIEW', remove: 'REMOVE' };

type MultiSelectOption = { value: string; label: string; color: string };

export type ShareMultiSelectorValue = MultiSelectOption[];

export type ShareSelectorOption = { value: string; label: string };

export const useOnShareDashboardModal = (onShareModalSuccess: () => void) => {
    const [showShareModal, setShowShareModal] = useState(false);
    const [activeModalDashboard, setActiveModalDashboard] = useState<Dashboard>();
    const [userGroupsWithAccess, setUserGroupsWithAccess] = useState<DashboardUserGroup[]>([]);
    const [aggreggableUserGroups, setAggreggableUserGroups] = useState<Option<DashboardUserGroup[]>>(none);
    const [selectedUserGroupsToAggreggate, setSelectedUserGroupsToAggreggate] = useState<ShareMultiSelectorValue>([]);
    const [dashboardAccessType, setDashboardAccessType] = useState<DashboardAccessVisibility | undefined>();
    const { read: readUserGroups } = dispatchUserGroups();
    const [selectedAccessType, setSelectedAccessType] = useState<ShareSelectorOption>({
        label: i18n.t('dashboards.share.view'),
        value: SHARE_OPTIONS.view,
    });
    const { userGroups, error, isLoading } = useUserGroups();
    const {
        update,
        success: hasUpdateSuccess,
        error: hasUpdateError,
        reset: resetUpdate,
        isLoading: isLoadingUpdate,
    } = useUpdateAccessDashboard();
    const access = activeModalDashboard?.access;
    const id = activeModalDashboard?.id;
    const isDashboardPublic = dashboardAccessType === DashboardAccessVisibility.PUBLIC;
    const hasDisclaimer = userGroupsWithAccess.some((userGroup: DashboardUserGroup) => userGroup.level === UserGroupLevel.EDIT);

    useEffect(() => {
        if (access && showShareModal) {
            setUserGroupsWithAccess(access.groups);
            setDashboardAccessType(access.visibility);
        }
    }, [access, showShareModal]);

    useEffect(() => {
        if (id && showShareModal) {
            readUserGroups();
        }
        if (!showShareModal && id) {
            return () => {
                setSelectedAccessType({
                    label: i18n.t('dashboards.share.view'),
                    value: SHARE_OPTIONS.view,
                });
                setSelectedUserGroupsToAggreggate([]);
                setUserGroupsWithAccess([]);
                setDashboardAccessType(undefined);
            };
        }
        return undefined;
    }, [id, showShareModal]);

    useEffect(() => {
        if (isSome(userGroups)) {
            if (userGroupsWithAccess.length > 0) {
                setAggreggableUserGroups(
                    some(
                        userGroups.value.filter(
                            (userGroup) => !userGroupsWithAccess.map((sortedUserGroup) => sortedUserGroup.id).includes(userGroup.id)
                        )
                    )
                );
            } else {
                setAggreggableUserGroups(userGroups);
            }
        } else {
            setAggreggableUserGroups(none);
        }
    }, [userGroups, userGroupsWithAccess]);

    useEffect(() => {
        if (hasUpdateSuccess) {
            onShareModalSuccess();
            setShowShareModal(false);
            resetUpdate();
        }
    }, [hasUpdateSuccess]);

    const handleOnShare = (dashboard: Dashboard) => {
        setActiveModalDashboard(dashboard);
        setShowShareModal(true);
    };

    const handleAddUserGroupToList = () => {
        if (selectedUserGroupsToAggreggate) {
            setUserGroupsWithAccess(
                userGroupsWithAccess.concat(
                    selectedUserGroupsToAggreggate.map((selectorValue) => ({
                        id: selectorValue.value,
                        name: selectorValue.label,
                        level: UserGroupLevel[selectedAccessType.value as UserGroupLevel],
                    }))
                )
            );
            setSelectedUserGroupsToAggreggate([]);
        }
    };

    const handleSelectAccessType = (selectorValue: ShareSelectorOption) => {
        setSelectedAccessType(selectorValue);
    };

    const handleUserGroupSelectChange = (multiSelectorValue: ShareMultiSelectorValue) => {
        setSelectedUserGroupsToAggreggate(multiSelectorValue);
    };

    const handleCheckMakePublic = () => {
        if (dashboardAccessType === DashboardAccessVisibility.PRIVATE) {
            setDashboardAccessType(DashboardAccessVisibility.PUBLIC);
        } else {
            setDashboardAccessType(DashboardAccessVisibility.PRIVATE);
        }
    };

    const handleOnChangeAccessType = (newAccess: string, groupId: string) => {
        const activeUserGroupIndex = userGroupsWithAccess.findIndex((userGroup: DashboardUserGroup) => userGroup.id === groupId);
        const newUserGroupsWithAccess = [...userGroupsWithAccess];
        if (newAccess === SHARE_OPTIONS.remove) {
            newUserGroupsWithAccess.splice(activeUserGroupIndex, 1);
        } else {
            newUserGroupsWithAccess[activeUserGroupIndex] = {
                ...newUserGroupsWithAccess[activeUserGroupIndex],
                level: UserGroupLevel[newAccess as UserGroupLevel],
            };
        }
        setUserGroupsWithAccess(newUserGroupsWithAccess);
    };

    const handleSaveShareChanges = () => {
        if (activeModalDashboard) {
            update(
                activeModalDashboard.copyWith({
                    access: { groups: userGroupsWithAccess, visibility: dashboardAccessType || DashboardAccessVisibility.PRIVATE },
                })
            );
        }
    };

    const handleOnClose = () => {
        setShowShareModal(false);
    };

    const onShareModal = (
        <DashboardShareModal
            data={aggreggableUserGroups}
            isLoading={isLoading}
            isLoadingUpdate={isLoadingUpdate}
            isPinned={activeModalDashboard?.pinned}
            handleSelectAccessType={handleSelectAccessType}
            error={error}
            errorAction={readUserGroups}
            tryAgain={handleSaveShareChanges}
            handleCancel={handleOnClose}
            hasDisclaimer={hasDisclaimer}
            hasUpdateError={hasUpdateError}
            hasUpdateSuccess={hasUpdateSuccess}
            userGroupsWithAccess={userGroupsWithAccess}
            handleUserGroupSelectChange={handleUserGroupSelectChange}
            handleAddUserGroupToList={handleAddUserGroupToList}
            selectedUserGroupsToAggreggate={selectedUserGroupsToAggreggate}
            selectedAccessType={selectedAccessType}
            handleCheckMakePublic={handleCheckMakePublic}
            isDashboardPublic={isDashboardPublic}
            handleSaveShareChanges={handleSaveShareChanges}
            handleOnChangeAccessType={handleOnChangeAccessType}
            showModal={showShareModal}
            handleCloseModal={handleOnClose}
        />
    );

    return { handleOnShare, onShareModal };
};
